import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { TypedMemo } from '../../../common/types';
import { MAILBOX_GUIDE_COPY } from './mailboxGuideCopy';
import { TILE_IDS, providerOf, tileOf } from './mailboxGuideState';
import { ProviderMark } from './ProviderMark';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProviderGridProps {
  /** The provider of the guide (detected or picked). Its tile gets the ring. */
  value: MailboxProviderId | null;
  onPick: (id: MailboxProviderId) => void;
}

/**
 * "Or pick where your email is" (design outreach-v2 p1, p2): 8 tiles of even height. A tile that is
 * not supported (Outlook, Microsoft 365) looks like the others and says "Not supported".
 */
const ProviderGridComponent: React.FC<ProviderGridProps> = ({ value, onPick }) => {
  const intl = useIntl();
  const on = tileOf(value);
  const tiles = useMemo(
    () =>
      TILE_IDS.map((id) => {
        const copy = MAILBOX_GUIDE_COPY[id];
        const unsupported = providerOf(id).passwordKind === 'unsupported';
        return (
          <button
            key={id}
            type="button"
            className={clsx(styles.tile, id === on && styles.tileOn)}
            aria-pressed={id === on}
            onClick={() => onPick(id)}
            data-testid={`provider-tile-${id}`}
          >
            <ProviderMark providerId={id} />
            <span className={styles.tileText}>
              <b>{intl.formatMessage(copy.label)}</b>
              {copy.tile !== null && (
                <small className={clsx(unsupported && styles.tileNo)}>{intl.formatMessage(copy.tile)}</small>
              )}
            </span>
          </button>
        );
      }),
    [intl, on, onPick]
  );
  return <div className={styles.grid}>{tiles}</div>;
};

export const ProviderGrid = TypedMemo(ProviderGridComponent);
